/**
 * Endpoint Relationships API service
 *
 * Provides functions for managing relationships between case endpoints.
 */

import { apiClient } from './api'
import type {
  EndpointRelationship,
  CreateRelationshipRequest,
  UpdateRelationshipRequest,
  MarkRelationshipOpenQuestionRequest,
  ResolveRelationshipOpenQuestionRequest,
  OpenQuestionsCount,
  BulkCreateRelationshipRequest,
  BulkCreateResponse,
  MoveRelationshipRequest,
  CopyRelationshipsRequest,
} from '../types/case-container'

// =============================================================================
// RELATIONSHIP API
// =============================================================================

/**
 * List all relationships for a case
 */
export async function listRelationships(caseId: string): Promise<EndpointRelationship[]> {
  return apiClient.get<EndpointRelationship[]>(`/cases/${caseId}/relationships`)
}

/**
 * List relationships marked as open questions
 */
export async function listOpenQuestions(caseId: string): Promise<EndpointRelationship[]> {
  return apiClient.get<EndpointRelationship[]>(`/cases/${caseId}/relationships/open-questions`)
}

/**
 * Get count of open questions for a case
 */
export async function countOpenQuestions(caseId: string): Promise<OpenQuestionsCount> {
  return apiClient.get<OpenQuestionsCount>(`/cases/${caseId}/relationships/open-questions/count`)
}

/**
 * Create a new relationship
 */
export async function createRelationship(
  caseId: string,
  relationship: CreateRelationshipRequest
): Promise<EndpointRelationship> {
  return apiClient.post<EndpointRelationship>(`/cases/${caseId}/relationships`, relationship)
}

/**
 * Update a relationship
 */
export async function updateRelationship(
  caseId: string,
  relationshipId: string,
  updates: UpdateRelationshipRequest
): Promise<EndpointRelationship> {
  return apiClient.patch<EndpointRelationship>(
    `/cases/${caseId}/relationships/${relationshipId}`,
    updates
  )
}

/**
 * Delete a relationship
 */
export async function deleteRelationship(caseId: string, relationshipId: string): Promise<void> {
  await apiClient.delete(`/cases/${caseId}/relationships/${relationshipId}`)
}

/**
 * Mark a relationship as an open question
 */
export async function markAsOpenQuestion(
  caseId: string,
  relationshipId: string,
  request?: MarkRelationshipOpenQuestionRequest
): Promise<EndpointRelationship> {
  return apiClient.post<EndpointRelationship>(
    `/cases/${caseId}/relationships/${relationshipId}/open-question`,
    request ?? {}
  )
}

/**
 * Resolve an open question
 */
export async function resolveOpenQuestion(
  caseId: string,
  relationshipId: string,
  request?: ResolveRelationshipOpenQuestionRequest
): Promise<EndpointRelationship> {
  return apiClient.post<EndpointRelationship>(
    `/cases/${caseId}/relationships/${relationshipId}/resolve`,
    request ?? {}
  )
}

// =============================================================================
// BULK OPERATIONS
// =============================================================================

/**
 * List all relationships where an endpoint is either source or target
 */
export async function listEndpointRelationships(
  caseId: string,
  endpointId: string
): Promise<EndpointRelationship[]> {
  return apiClient.get<EndpointRelationship[]>(
    `/cases/${caseId}/endpoints/${endpointId}/relationships`
  )
}

/**
 * Bulk create relationships from one source to multiple targets
 */
export async function bulkCreateRelationships(
  caseId: string,
  request: BulkCreateRelationshipRequest
): Promise<BulkCreateResponse> {
  return apiClient.post<BulkCreateResponse>(`/cases/${caseId}/relationships/bulk`, request)
}

/**
 * Move a relationship to a new source or target
 */
export async function moveRelationship(
  caseId: string,
  relationshipId: string,
  request: MoveRelationshipRequest
): Promise<EndpointRelationship> {
  return apiClient.patch<EndpointRelationship>(
    `/cases/${caseId}/relationships/${relationshipId}/move`,
    request
  )
}

/**
 * Copy all relationships from one endpoint to another
 */
export async function copyRelationships(
  caseId: string,
  request: CopyRelationshipsRequest
): Promise<BulkCreateResponse> {
  return apiClient.post<BulkCreateResponse>(`/cases/${caseId}/relationships/copy`, request)
}
